/**
 * Levenshtein Distance is used to calculate the difference between two words.
 *
 * https://en.wikipedia.org/wiki/Levenshtein_distance#Iterative_with_two_matrix_rows
 */
function levenshteinDistance(a: string, b: string): number {
    let v0: number[] = [];
    let v1: number[] = [];

    for (let i = 0; i <= b.length; i += 1) {
        v0[i] = i;
    }

    for (let i = 0; i < a.length; i += 1) {
        v1[0] = i + 1;

        for (let j = 0; j < b.length; j += 1) {
            const deletionCost = v0[j + 1] + 1;
            const insertionConst = v1[j] + 1;
            const substitutionCost = a[i] === b[j] ? v0[j] : v0[j] + 1;

            v1[j + 1] = Math.min(
                deletionCost,
                insertionConst,
                substitutionCost
            );
        }

        const tmp: number[] = v1;

        v1 = v0;
        v0 = tmp;
    }

    return v0[b.length];
}

// Jaro-Winkler Algorithm
function jaroWinkler(s1: string, s2: string): number {
    if (s1 === s2) return 1;

    const len1 = s1.length;
    const len2 = s2.length;
    const matchWindow = Math.floor(Math.max(len1, len2) / 2) - 1;

    const s1Matches = new Array(len1).fill(false);
    const s2Matches = new Array(len2).fill(false);

    let matches = 0;
    let transpositions = 0;

    // Find matches
    for (let i = 0; i < len1; i++) {
        const start = Math.max(0, i - matchWindow);
        const end = Math.min(i + matchWindow + 1, len2);

        for (let j = start; j < end; j++) {
            if (s2Matches[j] || s1[i] !== s2[j]) continue;
            s1Matches[i] = true;
            s2Matches[j] = true;
            matches++;
            break;
        }
    }

    if (matches === 0) return 0;

    // Count transpositions
    let k = 0;
    for (let i = 0; i < len1; i++) {
        if (!s1Matches[i]) continue;
        while (!s2Matches[k]) k++;
        if (s1[i] !== s2[k]) transpositions++;
        k++;
    }

    const jaro =
        (matches / len1 +
            matches / len2 +
            (matches - transpositions / 2) / matches) /
        3;

    // Add Winkler prefix bonus
    let prefix = 0;
    for (let i = 0; i < Math.min(len1, len2); i++) {
        if (s1[i] === s2[i]) prefix++;
        else break;
    }

    return jaro + 0.1 * prefix * (1 - jaro);
}

function jaccardSimilarity(str1: string, str2: string): number {
    const set1 = new Set(str1.toLowerCase().split(/\s+/));
    const set2 = new Set(str2.toLowerCase().split(/\s+/));

    const intersection = new Set([...set1].filter((x) => set2.has(x)));
    const union = new Set([...set1, ...set2]);

    return intersection.size / union.size;
}

function normalizeString(str: string): string {
    str = str.trim().toLowerCase()
                .replace(/[.,\-'"]/g, ' ')
                .replace(/\s+/g, ' ')
                .trim();

                return str;
}

/**
 * Returns a value that signifies how similar two strings are. A distance of 0 means that
 * the strings are identical.
 */
function distance(a: string, b: string): [number, number] {

    a = normalizeString(a)
    b = normalizeString(b)

    return [jaroWinkler(a, b), jaccardSimilarity(a, b)];
}

export { distance };
